import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { useOrganization } from "@/hooks/use-organization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import {
  JobOfferData,
  OfferWageSource,
} from "@/features/job-offer/utils/job-offer-data";

interface SaveAsTemplateDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  getFormValues: () => JobOfferData;
}

export function SaveAsTemplateDialog({
  isOpen,
  onOpenChange,
  getFormValues,
}: SaveAsTemplateDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const createMutation = useMutation(
    trpc.jobOffer.templateCreate.mutationOptions(),
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName("");
      setNameError(null);
    }
    onOpenChange(open);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Template name is required");
      return;
    }
    setNameError(null);

    const data = getFormValues();
    const usesOrganizationWageSet =
      data.wageSource === OfferWageSource.OrganizationWageSet &&
      !!data.organizationWageSetId;
    // A template names a wage set or holds wages, never both.
    const wages = usesOrganizationWageSet ? null : data;

    try {
      await createMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        name: trimmedName,
        offerTitle: data.title ?? null,
        offerDescription: data.description ?? null,
        facilityId: data.facility?.id ?? null,
        organizationWageSetId: usesOrganizationWageSet
          ? (data.organizationWageSetId ?? null)
          : null,
        regularWage: wages?.regularWage ?? null,
        regularOvertimeWage: wages?.regularOvertimeWage ?? null,
        weekendWage: wages?.weekendWage ?? null,
        weekendOvertimeWage: wages?.weekendOvertimeWage ?? null,
        onCallWage: wages?.onCallWage ?? null,
        onCallOvertimeWage: wages?.onCallOvertimeWage ?? null,
        onCallCallbackWage: wages?.onCallCallbackWage ?? null,
        onCallCallbackOvertimeWage: wages?.onCallCallbackOvertimeWage ?? null,
        holidayWage: wages?.holidayWage ?? null,
        holidayOvertimeWage: wages?.holidayOvertimeWage ?? null,
        wageFrequencyId: wages?.wageFrequencyId ?? null,
        timezoneId: data.timezoneId ?? null,
        schedules: (data.schedules ?? []).map((s) => ({
          startsAt: null,
          durationMinutes: s.durationMinutes,
          rrule: s.rrule ?? null,
          scheduleType: s.scheduleType,
        })),
        shareWithProviderPoolIds: data.providerPools?.map((p) => p.id) ?? [],
        shareWithUserIds: data.providerUsers?.map((u) => u.id) ?? [],
      });

      await queryClient.invalidateQueries(
        trpc.jobOffer.templateList.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );

      toast.success(`Template "${trimmedName}" saved`);
      handleOpenChange(false);
    } catch (err) {
      if (
        err instanceof Error &&
        err.message.toLowerCase().includes("already exists")
      ) {
        setNameError(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to save template",
        );
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save as Template</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <FormItem>
            <FormLabel className="font-medium">
              Template Name <span className="text-red-500">*</span>
            </FormLabel>
            <Input
              placeholder="e.g. Weekend ICU nights"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) {
                  setNameError(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
              autoFocus
            />
            {nameError ? <FormMessage>{nameError}</FormMessage> : null}
          </FormItem>

          <div className="flex gap-3 justify-end pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Template"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
